import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Stand } from './typen';
import type { Datenquelle } from './quelle';
import { RegelFehler } from './quelle';

export interface Meldung { id: number; text: string; art: 'ok' | 'fehler' | 'info' }

interface Kontext {
  stand: Stand;
  quelle: Datenquelle;
  neuLaden: () => Promise<void>;
  /** Führt eine Änderung aus, lädt neu und meldet Erfolg/Fehler. Rückgabe: true bei Erfolg */
  ausfuehren: (fn: () => Promise<unknown>, erfolg?: string) => Promise<boolean>;
  meldungen: Meldung[];
  melden: (text: string, art?: Meldung['art']) => void;
  /** Rückfrage im Seitenstil (ersetzt confirm, das im eingebetteten Betrieb nicht verfügbar ist) */
  fragen: (text: string, ok?: string, gefahr?: boolean) => Promise<boolean>;
  rueckfrage: Rueckfrage | null;
}
export interface Rueckfrage { text: string; ok: string; gefahr: boolean; antwort: (ja: boolean) => void }
const Ctx = createContext<Kontext | null>(null);

export function DatenProvider({ quelle, start, children }: { quelle: Datenquelle; start: Stand; children: ReactNode }) {
  const [stand, setStand] = useState<Stand>(start);
  const [meldungen, setMeldungen] = useState<Meldung[]>([]);
  const zaehler = useRef(0);
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
  const fragen = useCallback((text: string, ok = 'Bestätigen', gefahr = false) => new Promise<boolean>(resolve => {
    setRueckfrage({ text, ok, gefahr, antwort: (ja: boolean) => { setRueckfrage(null); resolve(ja); } });
  }), []);

  const melden = useCallback((text: string, art: Meldung['art'] = 'ok') => {
    const id = ++zaehler.current;
    setMeldungen(m => [...m, { id, text, art }]);
    setTimeout(() => setMeldungen(m => m.filter(x => x.id !== id)), art === 'fehler' ? 6500 : 3200);
  }, []);
  const neuLaden = useCallback(async () => { setStand(await quelle.laden()); }, [quelle]);

  useEffect(() => quelle.abonnieren(() => { void neuLaden(); }), [quelle, neuLaden]);

  const ausfuehren = useCallback(async (fn: () => Promise<unknown>, erfolg?: string) => {
    try {
      await fn();
      await neuLaden();
      if (erfolg) melden(erfolg, 'ok');
      return true;
    } catch (e) {
      melden(e instanceof RegelFehler ? e.message : `Fehler: ${(e as Error).message ?? e}`, 'fehler');
      await neuLaden();
      return false;
    }
  }, [neuLaden, melden]);

  const wert = useMemo(() => ({ stand, quelle, neuLaden, ausfuehren, meldungen, melden, fragen, rueckfrage }), [stand, quelle, neuLaden, ausfuehren, meldungen, melden, fragen, rueckfrage]);
  return <Ctx.Provider value={wert}>{children}</Ctx.Provider>;
}

export function useDaten(): Kontext {
  const c = useContext(Ctx);
  if (!c) throw new Error('DatenProvider fehlt');
  return c;
}
